import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { CheckCircle2, Link2, XCircle } from 'lucide-react';
import { Button, Card, EmptyState } from '@vwt/ui';
import { useApiMutation, useApiQuery } from '../../lib/api.js';
import { toastError } from '../../lib/errors.js';
import { useTranslation } from '../../lib/i18n.js';
import { useMe } from '../../lib/session.js';

/**
 * Explicit Slack account linking. The person reviews which Slack user is being linked before
 * confirming, which prevents a forwarded link from attaching someone else's Slack account.
 */
export function SlackLinkPage() {
  const { t } = useTranslation();
  const me = useMe();
  const [params] = useSearchParams();
  const state = params.get('state') ?? '';
  const details = useApiQuery('getSlackLink', { query: { state } }, { enabled: state.length >= 20, retry: false });
  const confirm = useApiMutation('confirmSlackLink');
  const [done, setDone] = useState(false);

  if (done) return <EmptyState icon={<CheckCircle2 className="text-success" />} title={t('slack.linkDone')} className="py-24" />;
  if (!state || details.isError) return <EmptyState icon={<XCircle className="text-danger" />} title={t('slack.linkInvalid')} className="py-24" />;
  if (!details.data) return null;
  return (
    <div className="flex justify-center p-6">
      <Card className="flex max-w-md flex-col gap-4 p-6">
        <h1 className="flex items-center gap-2 text-lg font-semibold">
          <Link2 className="size-5" /> {t('slack.linkTitle')}
        </h1>
        <p className="text-sm text-fg-muted">{t('slack.linkBody', { handle: `@${details.data.data.slackHandle}` })}</p>
        <p className="text-xs text-fg-subtle">
          {me.displayName} · {details.data.data.workspaceId}
        </p>
        <div className="flex justify-end">
          <Button
            variant="primary"
            loading={confirm.isPending}
            onClick={async () => {
              try {
                await confirm.mutateAsync({ body: { state } });
                setDone(true);
              } catch (error) {
                toastError(error);
              }
            }}
          >
            {t('slack.linkConfirm')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
